/**
 * 文字づかいの決めごと(スキーマ検証の土台)。
 *
 * 問題(`schema.ts`)と解説パート(`lesson.ts`)の**両方**が使うので、
 * どちらからも参照できるいちばん下の層に置いてある(循環 import を作らないため)。
 * 公開名は `schema.ts` から再輸出しているので、外から見える形は変わらない。
 */

/** 問題ID・単元ID・スロットIDに許す文字(英小文字始まりの英数字とハイフン)。 */
export const QUIZ_ID_PATTERN = /^[a-z][a-z0-9-]*$/;

/** 漢字(CJK統合漢字+々)。ふりがなを振れない場所では使わせない(CLAUDE.md ルール5)。 */
export const QUIZ_KANJI = /[々〆㐀-䶿一-鿿]/u;

/**
 * **漢字のかたまり1つ**に振るふりがなに許す文字(かなだけ)。
 *
 * 文まるごとの `QUIZ_RUBY` は数字も約物も含みうるが、区切りごとのふりがなは
 * 「その漢字の読み」そのものなので、かな以外が混ざっていたら切り分けを間違えている。
 * ゝゞヽヾ(繰り返し記号)も読みとして現れるので許す。
 */
export const QUIZ_RUBY_SEGMENT = /^[ぁ-んァ-ヶーゝゞヽヾ]+$/u;

/** ふりがなに許す文字。かな+約物+数字。 */
export const QUIZ_RUBY = /^[ぁ-んァ-ヶー0-9、。!?！？ \u3000]+$/u;

/**
 * 画像・音声アセットの相対パス。
 *
 * 配信JSONが持つのは **`assets/` からの相対パス**(例 `img/clock/clock-3-30.webp`)で、
 * URL はクライアントの `assetUrl()` が配信元(`VITE_ASSETS_BASE`)を前に足して組み立てる。
 * 絶対URL・先頭スラッシュ・`..` を混ぜられると、配信元をまたいだ読み込みや
 * アセット棚(asset-manifest)との食い違いが起きるので**形の時点で**弾く。
 */
export const QUIZ_ASSET_PATH = /^[a-z0-9][a-z0-9/._-]*\.[a-z0-9]+$/;

/** 相対パスとして安全か(形 + `..` を含まないこと)。 */
export function isQuizAssetPath(value: string): boolean {
  return QUIZ_ASSET_PATH.test(value) && !value.split('/').includes('..');
}

/** プレーンなオブジェクトか(配列・null を除く)。 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** 空でない文字列か。 */
export function isText(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * 制御文字(改行・タブ・NUL・DEL など)。
 *
 * 問題文・選択肢・解説は**1行のテキスト**として画面に出るので、
 * 生成器や翻訳の事故で混ざった制御文字はレイアウトを壊す(そして目で見えない)。
 * 漢字版フィールドのように**あとから機械生成で足す**ものほど、ここを形で止める。
 */
// 制御文字を**見つけるため**の正規表現なので、制御文字が入っているのが正しい。
// eslint-disable-next-line no-control-regex
export const QUIZ_CONTROL_CHARS = /[\u0000-\u001f\u007f]/u;
